// The delivery holding queue: turning the rows a provider returns into the figure and the labels a
// dashboard cell draws (IM-004c, sprint 11).
//
// Framework-free on purpose. Everything worth arguing about — how the headline count is arrived at,
// how a wait is worded, what a delivery belonging to no project is called — is here, where it can be
// tested without a dashboard, a DOM or a server.
//
// ⚠️ THE WAIT ITSELF IS NOT RECOMPUTED HERE. `DaysWaiting` arrives on the row, computed once
// server-side. That is deliberate, and it is the reminder widget's reasoning: a second implementation
// in the browser would eventually disagree with the first, because the browser's clock is the user's
// and a laptop an hour behind midnight in another time zone would count a different number of days.
// This file words that value; it never derives it.

/** One row as the provider returns it. Property names are PascalCase — the wire keeps C# casing. */
export interface DeliveryHoldingQueueRow {
    DeliveryId: number;
    Title?: string | null;
    DeliveryNoteNumber?: string | null;
    ReceivedDate?: string | null;
    DaysWaiting?: number | null;
    ProjectId?: number | null;
    ProjectNumber?: string | null;
    ProjectName?: string | null;
    ReceivingAddressName?: string | null;
    Status?: string | null;
    AllocationStatus?: string | null;
    TotalWaiting?: number | null;
}

/** Mirrors `KeyView.Procurement.DeliveryHoldingQueueWidgetConfig`. */
export interface DeliveryHoldingQueueConfig {
    Title?: string | null;
    ShowList?: boolean | null;
    ShowProject?: boolean | null;
    ShowReceivedAt?: boolean | null;
    EmptyText?: string | null;
}

/** Mirrors `DeliveryHoldingQueueWidgetConfig.NoProjectLabel`. */
export const NoProjectLabel = "No project";

/** Mirrors `DeliveryHoldingQueueWidgetConfig.NoDateLabel`. */
export const NoDateLabel = "No date recorded";

export const DefaultTitle = "Deliveries awaiting allocation";
export const DefaultEmptyText = "Nothing awaiting allocation";

/** What the widget needs to draw one cell. */
export interface DeliveryHoldingQueueView {
    /** The headline figure: everything waiting, not only what is listed. */
    total: number;
    /** The rows actually returned, longest-waiting first as the server ordered them. */
    rows: DeliveryHoldingQueueRow[];
    /** True when the server capped the listing, so the cell can say the list is partial. */
    truncated: boolean;
}

/**
 * Coerce whatever the host handed us into rows.
 *
 * A widget is handed `unknown`: a cell whose data has not arrived yet, a provider that soft-denied
 * and returned nothing, and a misconfigured binding all reach `render` the same way. Every one of
 * them should draw the empty state rather than throw inside a dashboard and take the page's other
 * cells with it.
 */
export function toRows(data: unknown): DeliveryHoldingQueueRow[] {
    if (Array.isArray(data))
        return data.filter(r => !!r) as DeliveryHoldingQueueRow[];

    const items = (data as { Items?: unknown } | null | undefined)?.Items;
    return Array.isArray(items) ? items.filter(r => !!r) as DeliveryHoldingQueueRow[] : [];
}

/**
 * The whole cell, from the rows.
 *
 * ⚠️⚠️ THE TOTAL COMES OFF THE ROW, NOT FROM `rows.length`. The server caps the listing and counts
 * separately, precisely so a backlog deeper than the cap still reports its real size — a queue of 600
 * reading as 50 would silently cancel the alarm this widget exists to raise. The row count is used
 * only as the fallback for an old payload that carries no total, and for the empty case where the two
 * agree by construction.
 */
export function buildQueueView(data: unknown): DeliveryHoldingQueueView {
    const rows = toRows(data);

    const declared = rows.find(r => typeof r.TotalWaiting === "number")?.TotalWaiting;
    const total = typeof declared === "number" && declared >= rows.length ? declared : rows.length;

    return { total, rows, truncated: total > rows.length };
}

/** The heading, with the widget's own default rather than a blank when an author cleared it. */
export function titleLabel(config: DeliveryHoldingQueueConfig | null | undefined): string {
    const title = config?.Title?.trim();
    return title ? title : DefaultTitle;
}

/** What the cell says when nothing is waiting. Deliberately reassuring, not blank. */
export function emptyText(config: DeliveryHoldingQueueConfig | null | undefined): string {
    const text = config?.EmptyText?.trim();
    return text ? text : DefaultEmptyText;
}

/** The delivery's own label, falling back to its note number and then to its id. */
export function titleOf(row: DeliveryHoldingQueueRow): string {
    const title = row?.Title?.trim();
    if (title) return title;

    const note = row?.DeliveryNoteNumber?.trim();
    if (note) return note;

    // ⚠️ Never an empty line. A row in this queue that draws as blank is indistinguishable from a
    // rendering fault, and the whole point of the cell is that somebody goes and finds the thing.
    return `Delivery ${row?.DeliveryId ?? "?"}`;
}

/**
 * How long it has been standing there.
 *
 * ⚠️ A null `DaysWaiting` is a real and common answer — nobody wrote down when it turned up — and it
 * is worded as such rather than shown as zero. Zero reads as "arrived today", the least urgent thing
 * in the queue, and an undated arrival is very often the oldest.
 */
export function waitingLabel(row: DeliveryHoldingQueueRow): string {
    const days = row?.DaysWaiting;
    if (typeof days !== "number") return NoDateLabel;

    if (days <= 0) return "Arrived today";
    if (days === 1) return "1 day waiting";
    return `${days} days waiting`;
}

/**
 * Which project it belongs to, or the "no project" label.
 *
 * ⚠️ A delivery with no project renders as {@link NoProjectLabel} rather than as a blank. A blank
 * reads as missing data; "no project" is the ordinary and expected state of an arrival nobody can
 * place yet (IM-004b), and the reader needs to be able to tell those apart.
 */
export function projectLabel(row: DeliveryHoldingQueueRow): string {
    const number = row?.ProjectNumber?.trim();
    const name = row?.ProjectName?.trim();

    if (number && name) return `${number} — ${name}`;
    if (number) return number;
    if (name) return name;
    return NoProjectLabel;
}

/** Where it is standing, or an empty string when nobody recorded that either. */
export function receivedAtLabel(row: DeliveryHoldingQueueRow): string {
    return row?.ReceivingAddressName?.trim() ?? "";
}
